import React from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, SearchOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Namespace } from '@data/index';
import { OrderDirection, TrialOrderBy } from '@data/sdk/match';
import {
  AntdInput,
  AntdSelect,
  AntdSkeleton,
  AntdSwitch,
  AntdTypography,
  Button,
} from '@ui/components';
import { SORT_OPTIONS } from './constants';

type TrialsSearchProps = {
  count: number | undefined;
  handleOrderByChange: (value: string) => void;
  handleOrderDirectionChange: (value: string) => void;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleStatusSwitchChange: (value: boolean) => void;
  isActiveStatus: boolean;
  orderBy: TrialOrderBy;
  orderDirection: OrderDirection;
  searchString: string;
};

const { Text } = AntdTypography;

export const TrialsSearch = ({
  count,
  orderBy,
  searchString,
  isActiveStatus,
  orderDirection,
  handleSearchChange,
  handleOrderByChange,
  handleStatusSwitchChange,
  handleOrderDirectionChange,
}: TrialsSearchProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);

  return (
    <div className="p-4 flex items-center flex-wrap gap-2">
      <div className="flex items-center flex-wrap gap-2">
        <AntdInput
          allowClear
          value={searchString}
          onChange={handleSearchChange}
          suffix={<SearchOutlined className="text-base-900" />}
          className="w-full md:w-[300px]"
          placeholder={t('trials_library.search.placeholder')}
        />
        <Text className="ml-2">{t('trials_library.trials_found')}: </Text>
        {typeof count === 'number' ? (
          <Text className="ml-1">{count}</Text>
        ) : (
          <AntdSkeleton.Button className="ml-1 h-4 w-2" />
        )}
      </div>
      <div className="ml-auto flex items-center">
        <Text className="mr-1">{t('trials_library.sort_by')}:</Text>
        <AntdSelect className="w-[200px]" onChange={handleOrderByChange} value={orderBy}>
          {SORT_OPTIONS.map((option) => (
            <AntdSelect.Option key={option.value} value={option.value}>
              {t(`trials_library.sorting.${option.label}`)}
            </AntdSelect.Option>
          ))}
        </AntdSelect>
        <Button
          type="text"
          className="gap-0 ml-1 px-2"
          onClick={() =>
            handleOrderDirectionChange(
              orderDirection === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc,
            )
          }
        >
          <ArrowUpOutlined
            className={cx('text-base-600', {
              'text-primary-600': orderDirection === OrderDirection.Asc,
            })}
          />
          <ArrowDownOutlined
            className={cx('text-base-600', {
              'text-primary-600': orderDirection === OrderDirection.Desc,
            })}
          />
        </Button>
      </div>
      <AntdSwitch
        value={isActiveStatus}
        onChange={handleStatusSwitchChange}
        checkedChildren={t('trials_library.status_switch.active')}
        unCheckedChildren={t('trials_library.status_switch.all')}
      />
    </div>
  );
};
